import React, { FC, useCallback, useMemo } from "react"
import { Button } from "dash-styled"
import {
    assignWorkerToApp,
    unassignWorkerFromApp,
    updateWorkerInApp
} from "../../../../../../Actions/API/WorkerActions"
import { useAppWorkerList } from "../../../../../../Hooks/useAppWorkerList"
import { useSelection } from "../../../../../../Hooks/useSelection"
import {
    APIWorker,
    getNumericWorkerId,
    getWorkerDisplayText
} from "../../../../../../Models/API/Worker"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { SortChooserProps } from "../../../../../Common/Button/SortChooser"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import { Table } from "resha"
import { TableHeader } from "../../../../../Common/Table/TableHeader"
import { defaultWorkerSort } from "../../Worker/Common/constants"
import { AppDetailBase } from "../AppDetailBase/AppDetailBase"
import { AssignModal } from "./Common/AssignModal"
import { AppWorkerRole } from "../../../../../../Models/API/AppWorkerRole"
import { useWorkerList } from "../../../../../../Hooks/useWorkerList"
import {
    APIAppWorker,
    getAppWorkerId
} from "../../../../../../Models/API/AppWorker"
import { appWorkerColumns, appWorkerSortOptions } from "./Common/constants"
import { AppWorkerRoleChangeModal } from "./Common/AppWorkerRoleChangeModal"
import { routePaths } from "../../../../../../Routes"
import { useModal } from "../../../../../../Hooks/useModal"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { getEmail } from "../../../../../../Helpers/Functions/Selectors/auth"
import { ReactComponent as ExternalLinkIcon } from "icons/lib/external-link.svg"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { Banner } from "../../../../../Common/Banner/Banner"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { Link } from "resha"

type Modal =
    | { type: "add" }
    | { type: "change-role"; worker: APIAppWorker }
    | { type: "remove"; worker: APIWorker }

const rowActions = ["Change role", "Remove from project"] as const

export const AppWorkerList: FC<{
    orgUsername: string
    appID: string
    sort: SortExpression<APIAppWorker>
}> = ({ orgUsername, appID, sort }) => {
    const myEmail = useAppState(getEmail)
    const dispatch = useDispatch()
    const instanceFetch = useAppInstance(appID)
    const rolesFetch = useAssumedRoles(appID)
    const [appWorkersFetch, loadMore] = useAppWorkerList(appID, sort)
    const { modal, open, close, save, isSaving } = useModal<Modal>()
    const sortOptions = useMemo<SortChooserProps<APIAppWorker>>(
        () => ({
            fields: appWorkerSortOptions,
            applied: sort,
            onChange: newSort =>
                dispatch(
                    push(routePaths.appWorkerList(orgUsername, appID, newSort))
                )
        }),
        [appID, dispatch, orgUsername, sort]
    )
    const [workersFetch, loadMoreWorkers, reloadWorkers] = useWorkerList(
        orgUsername,
        defaultWorkerSort
    )
    const handleRowAction = useCallback(
        (selected: typeof rowActions[number], id: number) => {
            const worker = getValue(appWorkersFetch)?.items.find(
                worker => worker.worker.id === id
            )

            if (worker === undefined) {
                return
            }

            switch (selected) {
                case "Remove from project":
                    open({
                        type: "remove",
                        worker: worker.worker
                    })
                    return
                case "Change role":
                    open({
                        type: "change-role",
                        worker
                    })
            }
        },
        [appWorkersFetch, open]
    )
    const columns = useMemo(
        () => appWorkerColumns([...rowActions], myEmail ?? "", handleRowAction),
        [handleRowAction, myEmail]
    )
    const handleRemove = useCallback(() => {
        if (modal?.type === "remove") {
            save(unassignWorkerFromApp(appID, modal.worker.id))
        }
    }, [appID, modal, save])
    const handleAdd = useCallback(() => open({ type: "add" }), [open])
    const handleAssign = useCallback(
        (ids: number[], role: AppWorkerRole) => {
            if (modal?.type === "add") {
                save(assignWorkerToApp(appID, ids, role))
            }
        },
        [appID, modal, save]
    )
    const handleSaveRole = useCallback(
        (role: AppWorkerRole) => {
            if (modal?.type === "change-role") {
                save(updateWorkerInApp({ ...modal.worker, role }))
            }
        },
        [modal, save]
    )
    const count = getValue(appWorkersFetch)?.total
    const [selected, toggleSelection] = useSelection(
        getAppWorkerId,
        getValue(appWorkersFetch)?.items ?? []
    )
    const selectedIds = useMemo(() => selected.map(getAppWorkerId), [selected])
    // TODO: this predicate only checks already fetched app workers.
    // We need endpoint to return workers that aren't assigned to this app.
    const isAlreadyAssigned = useCallback(
        (worker: APIWorker) =>
            getValue(appWorkersFetch)?.items.some(
                w => w.worker.id === worker.id
            ) ?? false,
        [appWorkersFetch]
    )
    const settingsRoute = routePaths.appSettings(orgUsername, appID)

    return (
        <AppDetailBase
            id={appID}
            orgUsername={orgUsername}
            title="Workers"
            breadcrumbs={["Workers"]}
            fixedContentWidth={false}
        >
            {hasAppPermission(getValue(rolesFetch) ?? [], "workers:invite") && (
                <Button
                    variant="contained"
                    className="w-48"
                    onClick={handleAdd}
                >
                    Add worker
                </Button>
            )}
            {hasAppPermission(getValue(rolesFetch) ?? [], "settings:update") &&
                hasValue(instanceFetch) && (
                    <Banner icon="info" className="mt-4">
                        {instanceFetch.value.canoticCrowd ? (
                            <>
                                Your data will be processed by super.AI’s
                                in-house workers. To process yourself, or with
                                people you invite,{" "}
                                <Link to={settingsRoute}>
                                    change your settings
                                </Link>
                            </>
                        ) : (
                            <>
                                Only the people listed below can process your
                                data. To use super.AI’s in-house workers
                                instead,{" "}
                                <Link to={settingsRoute}>
                                    change your settings
                                </Link>
                            </>
                        )}
                    </Banner>
                )}
            <TableHeader
                filteredCount={count}
                totalCount={count}
                sortOptions={sortOptions}
            />
            <div className="flex-grow h-0 flex-basis-0 overflow-y-auto -m-6 mt-0 px-6">
                <Table
                    className="w-full"
                    columns={columns}
                    getKey={getAppWorkerId}
                    items={getValue(appWorkersFetch)?.items}
                    itemCount={count}
                    stickyHeader={true}
                    loadMore={loadMore}
                    checkedItemIds={selectedIds}
                    onToggleRowCheck={toggleSelection}
                />
            </div>
            {modal?.type === "add" && (
                <AssignModal
                    resource="worker"
                    isSaving={isSaving}
                    models={getValue(workersFetch)?.items ?? []}
                    isAlreadyAssigned={isAlreadyAssigned}
                    renderModel={getWorkerDisplayText}
                    createLink={
                        <a
                            className="inline-block text-pink-500 no-underline hover:underline mt-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={routePaths.orgWorkerAdd(orgUsername)}
                        >
                            Create new worker
                            <ExternalLinkIcon className="inline-block ml-1 w-4 h-4 align-text-bottom" />
                        </a>
                    }
                    getId={getNumericWorkerId}
                    onClose={close}
                    onSubmit={handleAssign}
                    onLoadMore={loadMoreWorkers}
                    onReload={reloadWorkers}
                />
            )}
            {modal?.type === "change-role" && (
                <AppWorkerRoleChangeModal
                    title="Change worker role"
                    currentRole={modal.worker.role}
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleSaveRole}
                />
            )}
            {modal?.type === "remove" && (
                <Confirm
                    title={`Remove ${getWorkerDisplayText(modal.worker)}?`}
                    yesLabel={isSaving ? "Removing..." : "Remove"}
                    noLabel="Cancel"
                    isProcessing={isSaving}
                    onClose={close}
                    onClickYes={handleRemove}
                    onClickNo={close}
                />
            )}
        </AppDetailBase>
    )
}
